import { View, StyleSheet } from "react-native";
import { useState } from "react";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import CourseSettingsAccordion from "../../../components/adminDashboard/CourseSettingsAccordion/CourseSettingsAccordion";
import { InfoIcon, BookOpenIcon, CalendarBlankIcon } from "@phosphor-icons/react";
import AdminCourseInformationContainer from "../AdminCourseInformationContainer/AdminCourseInformationContainer";
import AdminCourseCatalogueInfoContainer from "../AdminCourseCatalogueInfoContainer/AdminCourseCatalogueInfoContainer";
import AdminDueDateControlsContainer from "../AdminDueDateControlsContainer/AdminDueDateControlsContainer";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import { radii, semanticColors, spacing, typographyScale } from "../../../styles/design-system";

interface AdminSettingsContainerProps {
  courseId?: string;
}

const getIconBackground = (color: string, icon: React.ReactNode) => (
  <View style={[styles.iconContainer, { backgroundColor: color }]}>{icon}</View>
);

const AdminSettingsContainer = ({ courseId }: AdminSettingsContainerProps) => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(0);

  const handleToggle = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <View style={styles.container}>
      <CustomSpacing type="vertical" size="m" />
      <CustomText useTranslationText={false} text="Course Settings" size="l" weight="bold" style={styles.title} />
      <CustomText
        useTranslationText={false}
        text="Configure course information, catalog details, and due dates"
        style={styles.description}
      />
      <CourseSettingsAccordion
        icon={getIconBackground("#bae6fd", <InfoIcon color="#0284C7" size={20} />)}
        title="Course Information"
        description="Basic course details and settings"
        expanded={expandedIndex === 0}
        onToggle={() => handleToggle(0)}>
        <AdminCourseInformationContainer courseId={courseId} />
      </CourseSettingsAccordion>

      <CourseSettingsAccordion
        icon={getIconBackground("#e8ddfb", <BookOpenIcon color="#7244bb" size={20} />)}
        title="Course Catalog Information"
        description="Public-facing course details for the catalog"
        expanded={expandedIndex === 1}
        onToggle={() => handleToggle(1)}>
        <AdminCourseCatalogueInfoContainer courseId={courseId} />
      </CourseSettingsAccordion>

      <CourseSettingsAccordion
        icon={getIconBackground("#e3fbcc", <CalendarBlankIcon color="#3b7c0f" size={20} />)}
        title="Due Date Controls"
        description="Manage deadlines and extensions for course activities"
        expanded={expandedIndex === 2}
        onToggle={() => handleToggle(2)}>
        <AdminDueDateControlsContainer courseId={courseId} />
      </CourseSettingsAccordion>
    </View>
  );
};

const styles = StyleSheet.create({
  title: {
    color: semanticColors.typography.displayPrimary,
    marginBottom: spacing.xxs,
  },
  description: {
    marginBottom: spacing.lg,
    color: semanticColors.typography.textPrimary,
    fontSize: typographyScale.textSm.fontSize,
  },
  container: {
    flex: 1,
    padding: spacing.lg,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: radii.lg,
    justifyContent: "center",
    alignItems: "center",
  },
});

export default AdminSettingsContainer;
